/**
 * Helpers for the org-scoped URL scheme.
 *
 * Every in-app route carries the active organization as its first path
 * segment -- ``/org_XXXX/scenarios?run=abc``. The org id is the Auth0
 * organization id, which always starts with ``org_``; that prefix is the
 * discriminator that lets us tell an org-scoped path apart from a legacy
 * org-less one (``/scenarios``).
 */

const ORG_ID_PREFIX = 'org_';

/**
 * localStorage key holding the last *validated* active org id (the org of the
 * decoded access token, written by ``useOrgClaims``). ``AuthProvider`` pins this
 * into the Auth0 client's ``authorizationParams`` at bootstrap so silent token
 * renewals stay in-org even when the landing URL is org-less. We use this
 * persisted, already-validated value -- not the raw URL path -- so a stale or
 * inaccessible org bookmark can't poison the bootstrap grant (ENG-1217).
 */
export const ACTIVE_ORG_STORAGE_KEY = 'antioch_active_org';

/**
 * Extract the org id from a pathname, or ``null`` when the first segment is
 * not an org id (legacy org-less path, root, Auth0 callback, etc.).
 *
 * :param pathname: the ``location.pathname`` to inspect
 * :return: the org id (``org_...``) or ``null``
 */
export function extractOrgId(pathname: string): string | null {
  const first = pathname.split('/')[1] ?? '';
  return first.startsWith(ORG_ID_PREFIX) ? first : null;
}

/**
 * Return ``pathname`` with the leading org segment removed, always starting
 * with ``/`` (root maps to ``/``). A pathname without an org prefix is
 * returned unchanged.
 *
 * :param pathname: the ``location.pathname`` to strip
 * :return: the org-less remainder (e.g. ``/org_X/scenarios`` -> ``/scenarios``)
 */
export function stripOrgPrefix(pathname: string): string {
  const orgId = extractOrgId(pathname);
  if (!orgId) return pathname;
  const rest = pathname.slice(`/${orgId}`.length);
  return rest === '' ? '/' : rest;
}

/**
 * Build an org-scoped path from an org id and an org-less route.
 *
 * :param orgId: the org id to prefix with
 * :param route: an org-less route, with or without a leading ``/`` (may carry
 *     a query string or hash, e.g. ``/scenarios?run=abc``)
 * :return: the org-scoped path (e.g. ``/org_X/scenarios?run=abc``)
 */
export function buildOrgPath(orgId: string, route: string): string {
  const normalized = route.startsWith('/') ? route : `/${route}`;
  return normalized === '/' ? `/${orgId}` : `/${orgId}${normalized}`;
}

/**
 * Prefix an org-less app path with the org from ``pathname`` (defaults to the
 * live URL). Used at ``<Link>`` / ``navigate`` boundaries where the org can be
 * read from the current URL -- org switching always reloads the page, so the
 * live pathname is always current within a session.
 *
 * :param path: the org-less target path (may carry a query string or hash)
 * :param pathname: the pathname to read the current org from
 * :return: the org-scoped path, or ``path`` unchanged when no org is present
 */
export function orgHref(path: string, pathname: string = window.location.pathname): string {
  const orgId = extractOrgId(pathname);
  return orgId ? buildOrgPath(orgId, path) : path;
}

/**
 * Whether a search string carries Auth0 callback params. The SDK needs these
 * to complete the token exchange (``code``) or surface failures (``error``),
 * so org-URL normalization must not rewrite the URL while they are present.
 *
 * :param search: the ``location.search`` to inspect
 * :return: ``true`` when ``code`` or ``error`` is present
 */
export function hasAuthCallbackParams(search: string): boolean {
  const params = new URLSearchParams(search);
  return params.has('code') || params.has('error');
}
